import { Desktop, Sun, Moon } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { PageHeader } from '@/components/ui/page-header';
import { SectionHeader } from '@/components/ui/section-header';
import { useTheme, type Theme } from '@/context/theme';

const THEME_OPTIONS: { value: Theme; label: string; icon: Icon }[] = [
  { value: 'system', label: 'System', icon: Desktop },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

export function SettingsPage() {
  const { theme, setTheme } = useTheme();

  return (
    <>
      <PageHeader
        title="Configuration"
        description="Portal preferences for this browser."
      />

      <SectionHeader title="Appearance" sub="· saved to this browser" />
      <div className="rounded-xl border border-border bg-surface p-5 shadow-(--c-shadow-sm)">
        <p className="mb-3 text-[13px] text-muted">
          Choose how the portal looks. System follows your device setting.
        </p>
        <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-canvas p-1">
          {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
            const active = theme === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setTheme(value)}
                aria-pressed={active}
                className={[
                  'inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                  active
                    ? 'bg-accent text-on-accent'
                    : 'text-muted hover:bg-accent-faint hover:text-text',
                ].join(' ')}
              >
                <Icon size={15} weight={active ? 'fill' : 'regular'} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <SectionHeader title="Documentation source" />
      <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-(--c-shadow-sm)">
        <Row label="Manifest file" value=".nmblr.portal.yaml" mono />
        <Row label="Blob store key" value="blobs/<sha256>" mono />
        <Row label="Sync queue" value="portal-sync (BullMQ)" mono last />
      </div>
    </>
  );
}

function Row({
  label,
  value,
  mono,
  last,
}: {
  label: string;
  value: string;
  mono?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={[
        'flex flex-col gap-1 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4',
        last ? '' : 'border-b border-border',
      ].join(' ')}
    >
      <span className="text-[13px] text-muted">{label}</span>
      <span
        className={[
          'text-[13px] text-text-2',
          mono ? 'font-mono text-[12.5px]' : '',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  );
}
